// components/vessel-sizing/MaterialData.js
// ASME Section II Part D allowable stress values and material data

export const allowableStressValues = {
  'SA-516-70': {
    '-30': 137.9, '50': 137.9, '100': 137.9, '150': 137.9,
    '200': 137.9, '250': 133.1, '300': 127.6, '350': 118.6,
    '400': 108.9, '450': 96.5,  '500': 82.7
  },
  'SA-106-B': {
    '-30': 137.9, '50': 137.9, '100': 137.9, '150': 137.9,
    '200': 137.9, '250': 137.9, '300': 137.9, '350': 133.1,
    '400': 127.6, '450': 106.9, '500': 81.4
  },
  'SA-240-304': {
    '-30': 138.0, '50': 138.0, '100': 138.0, '150': 126.2,
    '200': 117.2, '250': 110.3, '300': 104.8, '350': 100.0,
    '400': 96.5,  '450': 93.8,  '500': 91.0,  '550': 89.6
  },
  'SA-240-316': {
    '-30': 138.0, '50': 138.0, '100': 138.0, '150': 129.6,
    '200': 122.0, '250': 116.5, '300': 112.4, '350': 108.9,
    '400': 106.2, '450': 104.1, '500': 102.0, '550': 100.7
  },
  'SA-240-321': {
    '-30': 138.0, '50': 138.0, '100': 138.0, '150': 126.9,
    '200': 118.6, '250': 112.4, '300': 107.6, '350': 103.4,
    '400': 100.0, '450': 96.5,  '500': 93.1,  '550': 89.6
  },
  'SA-387-11': {
    '-30': 172.4, '50': 172.4, '100': 172.4, '150': 172.4,
    '200': 172.4, '250': 172.4, '300': 172.4, '350': 172.4,
    '400': 165.5, '450': 149.6, '500': 129.6, '550': 102.1
  },
  'SA-387-22': {
    '-30': 172.4, '50': 172.4, '100': 172.4, '150': 172.4,
    '200': 172.4, '250': 172.4, '300': 172.4, '350': 172.4,
    '400': 172.4, '450': 155.8, '500': 135.8, '550': 103.4
  },
};

export const materialDensities = {
  'SA-516-70': 7850, 'SA-106-B': 7850,
  'SA-240-304': 8000, 'SA-240-316': 8000, 'SA-240-321': 8000,
  'SA-387-11': 7850, 'SA-387-22': 7850,
};

export const materialCostFactors = {
  'SA-516-70': 1.0, 'SA-106-B': 1.1,
  'SA-240-304': 2.5, 'SA-240-316': 3.0, 'SA-240-321': 3.2,
  'SA-387-11': 1.7, 'SA-387-22': 2.0,
};

export const materialInfo = {
  'SA-516-70': {
    name: 'Carbon Steel (SA-516 Gr. 70)',
    applications: 'General pressure vessels, non-corrosive service',
    limitations: 'Not for sour service or T > 450°C',
  },
  'SA-106-B': {
    name: 'Carbon Steel Pipe (SA-106 Gr. B)',
    applications: 'Piping and small diameter vessels',
    limitations: 'Not for sour service or T > 450°C',
  },
  'SA-240-304': {
    name: 'Stainless Steel 304 (SA-240)',
    applications: 'Corrosive services, food/chemical processing',
    limitations: 'Susceptible to chloride SCC above 60°C',
  },
  'SA-240-316': {
    name: 'Stainless Steel 316 (SA-240)',
    applications: 'Higher corrosion resistance than 304, better Cl⁻ resistance',
    limitations: 'Still susceptible to SCC at elevated temps',
  },
  'SA-240-321': {
    name: 'Stainless Steel 321 (SA-240, Ti-stabilised)',
    applications: 'High temperature, sensitization resistance',
    limitations: 'Higher cost, harder to fabricate',
  },
  'SA-387-11': {
    name: 'Alloy Steel 1¼Cr-½Mo (SA-387 Gr. 11)',
    applications: 'Elevated temperature, hydrogen service',
    limitations: 'PWHT required, limited corrosion resistance',
  },
  'SA-387-22': {
    name: 'Alloy Steel 2¼Cr-1Mo (SA-387 Gr. 22)',
    applications: 'High temp / high H₂ partial pressure, hydroprocessing reactors',
    limitations: 'PWHT required, higher cost than Gr. 11',
  },
};

export const standardPlateThicknesses = [
  6, 8, 10, 12, 14, 16, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40, 45, 50,
];

export function getInterpolatedStress(materialCode, temperature) {
  const stressData = allowableStressValues[materialCode];
  if (!stressData) return 0;
  const temps = Object.keys(stressData).map(Number).sort((a, b) => a - b);
  if (temperature <= temps[0]) return stressData[temps[0]];
  if (temperature >= temps[temps.length - 1]) return stressData[temps[temps.length - 1]];
  for (let i = 0; i < temps.length - 1; i++) {
    if (temps[i] <= temperature && temps[i + 1] > temperature) {
      const lo = stressData[temps[i]], hi = stressData[temps[i + 1]];
      return lo + (hi - lo) * (temperature - temps[i]) / (temps[i + 1] - temps[i]);
    }
  }
  return stressData[temps[0]];
}

export function getStandardThickness(required) {
  for (const t of standardPlateThicknesses) if (t >= required) return t;
  return standardPlateThicknesses[standardPlateThicknesses.length - 1];
}

export function recommendMaterial(designTemp, designPressure, h2sPresent, co2Present, chloridePresent) {
  let mat = 'SA-516-70';
  if (h2sPresent && designPressure > 100) mat = 'SA-387-11';
  if (h2sPresent && designTemp > 300) mat = 'SA-240-316';
  if (co2Present && designTemp > 150) mat = 'SA-240-316';
  if (chloridePresent && designTemp > 60) mat = 'SA-240-316';
  if (designTemp > 450) mat = designTemp > 550 ? 'SA-240-321' : 'SA-387-22';
  return mat;
}
